import { OperationVariables, useMutation } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  CreateCurationContentPackListMutation,
  CreateCurationContentPackListMutationVariables,
} from '../../../../../../__gqltypes__';
import mutations from '../../../utils/mutations';

type Props<T> = {
  refetchLists: (variables?: Partial<OperationVariables> | undefined) => Promise<T>;
  setActiveList: (listId: string) => void;
};

const SPPCreatePackList = <T,>({ refetchLists, setActiveList }: Props<T>) => {
  const [createPackList] = useMutation<
    CreateCurationContentPackListMutation,
    CreateCurationContentPackListMutationVariables
  >(mutations.CREATE_CURATION_CONTENT_PACK_LIST);

  const [creating, setCreating] = useState(false);
  const askCreate = useCallback(() => setCreating(true), [setCreating]);
  const cancelCreate = useCallback(() => setCreating(false), [setCreating]);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');

  const nameFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creating && nameFieldRef.current !== null) {
      nameFieldRef?.current.focus();
    } else {
      setName('');
    }
  }, [creating]);

  const doCreate = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitting(true);
      let res;
      try {
        res = await createPackList({
          variables: {
            input: {
              name,
            },
          },
        });
      } catch (err) {
        console.error(err);
        setSubmitting(false);
        return;
      }
      setCreating(false);
      await refetchLists();
      setSubmitting(false);
      if (res.data) {
        setActiveList(res.data.createCurationContentPackList.id);
      }
    },
    [createPackList, refetchLists, setActiveList, name]
  );

  return (
    <>
      <Button className="w-100" variant="success" onClick={askCreate}>
        <i className="fas fa-plus mr-2" />
        New pack list
      </Button>

      <Modal show={creating} onHide={cancelCreate}>
        <Modal.Header closeButton>
          <Modal.Title>Create pack list</Modal.Title>
        </Modal.Header>
        <Form onSubmit={doCreate}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control autoFocus value={name} onChange={(e) => setName(e.target.value)} ref={nameFieldRef} />
              <span>This must be unique.</span>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancelCreate} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting && <Spinner as="span" animation="border" size="sm" className="mr-2" />}
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SPPCreatePackList;
